/* eslint-disable max-lines */
import * as React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import breakpoints from '@westwing/ui-kit/breakPoints';
import { useRef, useMemo, useCallback } from 'react';
import { HeaderModeType, Tabs } from '../types/Header';
import { clubTheme } from './theme';
import type HeaderVariants from './HeaderVariants.json';
import ScopedStyleProvider from './ScopedStyleProvider';
import NavigationBar, { navigationBarHeight } from './NavigationBar';
import { HeaderStandaloneProps } from './types/headerStandalone';
import { useExternalProps } from './hooks/useExternalProps';
import { generateWWEvents } from './WWEvents';
import { ClubContextProvider } from './hooks/ClubContext';
import { prefixClubLinks } from './clubLinks';
import useCurrentPageType from './hooks/useCurrentPageType';
import createHeaderProps from './createHeaderProps';
import { RaFPromotionalBanner } from './RaFPromotionalBanner';
import { BurgerMenuContent } from './BurgerMenuContent';
import { OneCartContextProvider } from './hooks/OneCartContext';
import Header from 'Components/Header';
import { HeaderContextProvider } from 'Components/HeaderContext';
import { Logo } from 'Components/Logo';
import { MockedStickyOverlayLine } from 'Components/Header/HeaderElements';
import ErrorBoundary from 'Components/ErrorBoundary';
import { OnboardingTooltipConfig } from 'types/OnboardingTooltip';
import { OnboardingStarsIcon, OnboardingRoomPlannerIcon } from 'Components/OnboardingTooltip';
import { infoBlockHeight } from 'Components/InfoBlock';

const StyledMain = styled.div`
    font-family: ${({ theme }) => theme.fonts.fontBrandonRegular};
    background-color: ${({ theme }) => theme.colors.white};
`;

const getStickyHeaderStyles = (
    theme: DefaultTheme,
    isStickyEnabled = false,
    scope: string,
    stickyHeight?: string | null
    // eslint-disable-next-line max-params
) =>
    `
        #${scope} {
            z-index: ${theme.zindexOneHeaderNavBar};
            position: ${isStickyEnabled ? 'sticky' : 'relative'};
            top: ${isStickyEnabled ? stickyHeight ?? `-${theme.owwHeader.mobileRowHeightSelected}px` : 'unset'};
        }

        @media (min-width: ${breakpoints.breakpointTablet}) {
            #${scope} {
                top: ${isStickyEnabled ? stickyHeight ?? `-${theme.owwHeader.tabletRowHeightSelected}px` : 'unset'};
            }
        }

        @media (min-width: ${breakpoints.breakpointDesktop}) {
            #${scope} {
                top: ${
                    isStickyEnabled
                        ? `-${theme.owwHeader.desktopRowHeight + infoBlockHeight + navigationBarHeight + 1}px`
                        : 'unset'
                };
            }
        }
    `;

const getHeaderHeightGlobalCSSVariables = (theme: DefaultTheme) => `
    :root {
        --oww-header-first-row-height: ${theme.owwHeader.mobileRowHeight}px;
    }
    @media (min-width: ${breakpoints.breakpointDesktop}) {
        :root {
            --oww-header-first-row-height: ${theme.owwHeader.desktopRowHeight}px;
        }
    }
`;

// TODO: remove Partial and pass all needed props
// eslint-disable-next-line complexity
const HeaderStandalone = ({
    shouldFetchData = false,
    stickyAreaContainer,
    linkPrefix,
    serverSide,
    bellaStickyAreaContainer,
    useNewLogo = false,
    styleVariant,
    ...baseProps
}: Omit<HeaderStandaloneProps, 'burgerMenuOnClick'>): JSX.Element => {
    const links = prefixClubLinks(linkPrefix);
    const [isBurgerMenuOpen, setIsBurgerMenuOpen] = React.useState<boolean>(false);
    const burgerMenuOnClick = (open: boolean) => setIsBurgerMenuOpen(open);
    const props = useExternalProps({ ...baseProps, linkPrefix, serverSide, burgerMenuOnClick }, links, shouldFetchData);
    const headerProps = useMemo(() => createHeaderProps(props), [props]);
    const {
        variant = 'default',
        selectedNavigationItem,
        t,
        scope,
        logger,
        trackingProvider,
        loginState,
        noOverlayForCOPNav,
        iconTrayVisibility,
        smartAppBannerUrl,
        isOpenClubEnabled,
        promotionalBannerDisplay = 'none',
        isBurgerMenuMyWWStyleItemEnabled = false,
        isOneCart,
        country,
        fetchCartDataOnce,
        voucherMenuUrl = '',
        clubCiVersion,
    } = props;
    const stickyHeaderStyle = useRef(
        getStickyHeaderStyles(clubTheme, true, scope, headerProps.headerMode === HeaderModeType.MINI ? '0' : null)
    );
    const headerHeightGlobalCSSVariables = useMemo(() => getHeaderHeightGlobalCSSVariables(clubTheme), []);
    const stickyRef = useRef<HTMLDivElement>(null);
    const [isLogoVisible, setIsLogoVisible] = React.useState<boolean>(false);
    const [onboardingTooltipConfig, setOnboardingTooltipConfig] = React.useState<OnboardingTooltipConfig>();
    const fetchCartDataFlag = fetchCartDataOnce ?? useRef<boolean>(false);

    const showTooltip = useCallback(() => {
        setOnboardingTooltipConfig({
            show: true,
            tooltipFor: Tabs.Shop,
            headerText: t('OwwHeader_Shop tooltip headline'),
            text: t('OwwHeader_Shop tooltip text'),
            TooltipIcon: <OnboardingStarsIcon />,
            trackingAction: 'Shop Onboarding Tooltip',
            trackingLabel: 'Existing user',
        });
    }, [t]);

    const showNewUserTooltip = useCallback(() => {
        setOnboardingTooltipConfig({
            show: true,
            tooltipFor: Tabs.Shop,
            headerText: t('OwwHeader_Shop new user tooltip headline'),
            text: t('OwwHeader_Shop new user tooltip text'),
            TooltipIcon: <OnboardingRoomPlannerIcon />,
            trackingAction: 'Shop Onboarding Tooltip',
            trackingLabel: 'New User',
        });
    }, [t]);

    React.useEffect(() => {
        const WWEvents = generateWWEvents();
        WWEvents.push({ event: 'SHOW_SHOP_ONBOARDING_TOOLTIP', listener: showTooltip });
        WWEvents.push({ event: 'SHOW_SHOP_NEW_USER_ONBOARDING_TOOLTIP', listener: showNewUserTooltip });
        return () => {
            WWEvents.remove('SHOW_SHOP_ONBOARDING_TOOLTIP', showTooltip);
            WWEvents.remove('SHOW_SHOP_NEW_USER_ONBOARDING_TOOLTIP', showNewUserTooltip);
        };
    }, [showTooltip, showNewUserTooltip]);

    React.useEffect(() => {
        if (!stickyAreaContainer || !stickyRef.current) {
            return;
        }
        stickyRef.current.appendChild(stickyAreaContainer);
    }, [stickyAreaContainer]);

    const stickyChangeHandler = (isVisible: boolean) => {
        setIsLogoVisible(isVisible);
    };

    return (
        <ScopedStyleProvider theme={clubTheme} scope={scope}>
            <HeaderContextProvider
                value={{
                    app: 'CLUB',
                    t,
                    logger,
                    trackingHelpers: { ...trackingProvider, useCurrentPageType, segment: { track: () => null } }, // TODO: pass segment tracking
                    useNewLogo,
                }}
            >
                <ClubContextProvider value={{ links, serverSide, isOpenClubEnabled, clubCiVersion }}>
                    <OneCartContextProvider
                        isEnabled={isOneCart}
                        country={country}
                        logger={logger}
                        fetchCartDataOnce={fetchCartDataFlag}
                        styleVariant={styleVariant || 'default'}
                    >
                        {/* eslint-disable-next-line react/no-danger */}
                        <style dangerouslySetInnerHTML={{ __html: stickyHeaderStyle.current }} />
                        {/* eslint-disable-next-line react/no-danger */}
                        <style dangerouslySetInnerHTML={{ __html: headerHeightGlobalCSSVariables }} />
                        <ErrorBoundary fallbackComponent={null}>
                            <StyledMain data-testid="oww-header">
                                <Header
                                    isDocumentCookie={false}
                                    shouldShowAppBanner={headerProps.shouldShowAppBanner}
                                    smartAppBannerUrl={smartAppBannerUrl}
                                    tabsConfig={headerProps.tabsConfig}
                                    burgerMenuConfig={headerProps.burgerMenuConfig}
                                    iconTrayComponents={
                                        iconTrayVisibility === 'hidden' ? [] : headerProps.iconTrayComponents
                                    }
                                    iconTrayVisibility={iconTrayVisibility}
                                    middleAreaComponent={
                                        <>
                                            <MockedStickyOverlayLine $isHidden={!isLogoVisible}>
                                                <Logo $appear $isHidden={!isLogoVisible} href={headerProps.logoHref} />
                                            </MockedStickyOverlayLine>
                                            {headerProps.middleAreaComponent}
                                        </>
                                    }
                                    afterHeaderComponent={
                                        <NavigationBar
                                            noOverlayForCOPNav={noOverlayForCOPNav}
                                            isLoggedIn={loginState.isLoggedIn && !loginState.isPartiallyLoggedIn}
                                            selectedNavigationItem={selectedNavigationItem}
                                        />
                                    }
                                    isStickyEnabled={variant !== 'top_only'}
                                    infoBlockComponents={headerProps.infoBlockComponents}
                                    headerMode={headerProps.headerMode}
                                    miniHeaderConfig={headerProps.miniHeaderConfig}
                                    stickyAreaComponents={[
                                        bellaStickyAreaContainer ?? <div ref={stickyRef} key="sticky-area" />,
                                        <RaFPromotionalBanner
                                            key="promotional-banner"
                                            display={promotionalBannerDisplay}
                                        />,
                                    ]}
                                    logoHref={headerProps.logoHref}
                                    onStickyChange={stickyChangeHandler}
                                    isStuck={isLogoVisible}
                                    onboardingTooltipConfig={onboardingTooltipConfig}
                                />
                                {!serverSide && isBurgerMenuOpen && (
                                    <BurgerMenuContent
                                        isLoggedIn={
                                            (loginState?.isLoggedIn || loginState?.isPartiallyLoggedIn) ?? false
                                        }
                                        isBurgerMenuMyWWStyleItemEnabled={isBurgerMenuMyWWStyleItemEnabled}
                                        voucherMenuUrl={voucherMenuUrl}
                                        smartAppBannerUrl={smartAppBannerUrl}
                                        onClose={() => burgerMenuOnClick(false)}
                                        firstRowProps={{
                                            logoHref: headerProps.logoHref,
                                            tabsConfig: headerProps.tabsConfig,
                                            onboardingTooltipConfig,
                                            isStickyHeaderActive: isLogoVisible,
                                            logoConfigLinkComponent: headerProps.logoConfig?.linkComponent,
                                        }}
                                        country={country}
                                    />
                                )}
                            </StyledMain>
                        </ErrorBoundary>
                    </OneCartContextProvider>
                </ClubContextProvider>
            </HeaderContextProvider>
        </ScopedStyleProvider>
    );
};

// Exposes the header for Club to use
export default HeaderStandalone;

export type HeaderVariant = keyof typeof HeaderVariants;
